import React from 'react';

/**
 * Inline source chip embedded in PA prose, e.g. `EMAIL Apr 15`.
 * Backs up every PA claim with a pointer to memory or an entity.
 *
 * Keep rendering even when metadata is sparse; at worst it shows the source label.
 */

export type CitationSource = 'email' | 'todo' | 'note' | 'memory';

export interface CitationProps {
  source: CitationSource;
  label?: string;
  date?: string;
  title?: string;
  onOpen?: () => void;
  show?: boolean;
}

const SOURCE_LABEL: Record<CitationSource, string> = {
  email: 'EMAIL',
  todo: 'TODO',
  note: 'NOTE',
  memory: 'MEM',
};

export function Citation({
  source,
  label,
  date,
  title,
  onOpen,
  show = true,
}: CitationProps) {
  if (!show) return null;

  const interactive = typeof onOpen === 'function';
  const tail = label ?? date ?? source;

  const content = (
    <>
      <span
        style={{
          padding: '0 4px',
          borderRadius: 3,
          color: 'var(--accent)',
          background: 'var(--accent-wash)',
          fontSize: '0.72em',
          lineHeight: 1.4,
          letterSpacing: '0.35px',
        }}
      >
        {SOURCE_LABEL[source] ?? 'SOURCE'}
      </span>
      {date && <span>{date}</span>}
      {label && !date && <span>{label}</span>}
    </>
  );

  const commonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '1px 7px 2px',
    margin: '0 1px',
    fontSize: '0.82em',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-secondary)',
    background: 'var(--accent-wash)',
    borderRadius: 4,
    whiteSpace: 'nowrap',
    verticalAlign: 'baseline',
    letterSpacing: '0.2px',
  };

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onOpen}
        title={title ?? tail}
        className="signa-citation"
        style={{
          ...commonStyle,
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      className="signa-citation"
      title={title ?? tail}
      style={commonStyle}
    >
      {content}
    </span>
  );
}
